import { Button } from '@mui/material';
import React, { FC, PropsWithChildren } from 'react';

import useIsMobile from '../../../../hooks/useIsMobile';
import { InputFileProps } from '../../../../models/attachment';

import MuiIcon from '../../MuiIcon';
import MaterialSymbolFolderNewIcon from '../../MuiIcon/MaterialSymbolIcon/FolderNew';

import * as Styled from '../styled';

export type Props = PropsWithChildren<{
  inputFileProps: InputFileProps;
  inputId: string;
  isDisabled: boolean;
}>;

/**
 * The input for attaching files
 *
 * @param props                Props passed to the component
 * @param props.children       The children of the component (button label)
 * @param props.inputFileProps Props passed to the <input type="file" />
 * @param props.inputId        The id of the input
 * @param props.isDisabled     Whether the input is disabled
 * @returns                    The component itself
 */
const InputFileAttachment: FC<Props> = ({
  children,
  inputFileProps,
  inputId,
  isDisabled,
}) => {
  const isMobile = useIsMobile();

  const wrapperProps = {
    disabled: isDisabled,
    htmlFor: inputId,
    tabIndex: -1,
  };

  const input = (
    <Styled.FileUploadInput id={inputId} type="file" {...inputFileProps} />
  );

  if (isMobile) {
    return (
      <Styled.AttachmentLabel {...wrapperProps}>
        {input}
        <MuiIcon color="primary" icon="attachFile" />
      </Styled.AttachmentLabel>
    );
  }

  return (
    <Button
      component="label"
      startIcon={<MaterialSymbolFolderNewIcon />}
      variant="contained"
      {...wrapperProps}
    >
      {children}
      {input}
    </Button>
  );
};

export default InputFileAttachment;
